import { ListPlus, Percent } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
} from '@/components/ui/Card'
import { Field, Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { useDocumentTemplates } from '@/features/directories/api/useDirectories'
import { useAddCostLines, useApplyMarkup } from '@/features/quotes/api/useQuote'
import { formatNumber } from '@/lib/format'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { QuoteDetail } from '@/types/domain'

/* Pricing a costing from its cost side (FR-1), the way the source workbook does.
 *
 * ── The two percentages, and why they are not one ────────────────────────────
 *
 * The workbook's "Profit Margin 0.05" is a MARKUP OVER COST: 9 600 × 1.05 =
 * 10 080. The figure the quote footer shows is a MARGIN OVER SELL, which on the
 * same costing reads 4.76%. They are different numbers describing the same
 * deal, and a panel that printed either of them under the other's name would
 * look like a form that cannot count. So both are on screen, each under its own
 * label: the markup is what the reader TYPES, the margin is what the server
 * answers with.
 *
 * ── Nothing here does arithmetic ─────────────────────────────────────────────
 *
 * Not one figure below is computed in this file. The cost, the customer total
 * and the margin all come off `record.costTotals`, which the server recomputes
 * on every read — because the margin a salesperson prices against must not
 * depend on which screen they read it from, and two surfaces that each add up
 * the same lines will eventually disagree by a rounding rule.
 *
 * The markup goes up as a STRING for the same reason the money does (AD-9): a
 * JSON number arrives as a float64 and prices a costing at 33.329999999999998%.
 *
 * One figure is READ here without being shown, and the distinction is worth
 * stating because it looks like the exception to the paragraph above: the cost
 * total decides whether Apply is pressable at all. That is a guard, not
 * arithmetic — nothing is derived from it and nothing is rendered from it —
 * and `noCostSide` below says what it prevents.
 *
 * ── Why the preset button lives here ─────────────────────────────────────────
 *
 * A costing starts from the same seven charge lines on nearly every quotation,
 * and typing them out is what the workbook's pre-written rows spared people. The
 * labels are maintained as `cost-item` templates in Settings, and this surface
 * reads them and posts them as WORDS. `internal/quote` may not query
 * `document_templates` (AD-1, AD-2) — the batch endpoint is generic and knows
 * nothing about templates, exactly as the not-included block picker's insertion
 * does not.
 *
 * They are inserted with no amounts. A rate stored on a template would be last
 * quarter's, read next quarter as though it were current; the money is entered
 * per quote, which is the whole reason a preset is a list of labels.
 */

/* The three markups an operator reaches for without thinking.
 *
 * Not configurable, and not read from Settings: they are a KEYBOARD SHORTCUT for
 * the field beside them, not a policy. A quote priced at 12.5% is typed, and
 * nothing here refuses it — a chip set that could be mistaken for the allowed
 * values would be a rule this product does not have.
 *
 * Strings, because that is what the field holds and what goes on the wire
 * (AD-9): a numeric 10 would have to be formatted back into the input, and the
 * formatting is where a locale's comma gets in. */
const MARKUP_PRESETS = ['10', '15', '20'] as const

export function MarginPanel({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const markupMutation = useApplyMarkup(record.id)
  const presetMutation = useAddCostLines(record.id)
  const { data: templates } = useDocumentTemplates()

  const [markup, setMarkup] = useState('')

  const totals = record.costTotals
  const lines = record.costLines ?? []
  const preset = (templates ?? []).filter((template) => template.kind === 'cost-item')

  /* A percentage the server will accept: digits, optionally with a decimal part.
     A comma is allowed because that is what a Russian keyboard produces, and the
     mutation normalises it to a dot before it goes on the wire. */
  const markupUsable = /^\d+([.,]\d+)?$/.test(markup.trim())

  /* Bare `disabled` rather than a blockedReason on both buttons below, which is
     the exception this codebase's Button documents rather than the rule: the
     cause is already on screen in each case — the sentence under the row says
     the costing is empty, or that Settings holds no standing charge lines. */
  const nothingToPrice = lines.length === 0
  const nothingToInsert = preset.length === 0

  /* The second refusal, and it is a GUARD rather than a fourth figure — which
     is the only reason a file that computes nothing reads a total at all.
     Nothing is derived from this number and nothing is shown from it; it
     decides whether a button may be pressed.

     What it prevents: the server prices sell = buy × (1 + markup/100), per
     line. On a costing whose cost side is zero that is sell = 0 on every line,
     so pressing Apply would replace a customer total somebody typed with
     0.00 — the destructive answer to a button labelled "Apply", arrived at
     silently and with no undo.

     It fires on far fewer costings than it used to, and the guard is kept
     exactly as it was regardless. The cost-line form collects the unit COST now
     and `toCostLinePayload` sends it as both sides, so a line somebody typed
     carries a real cost; what still reaches this state is a costing of PRESET
     lines nobody has priced, inserted at zero by the button below. Pressing
     Apply on those would be the same silent wipe of nothing into nothing, and
     the sentence beside the button still names the reason correctly.

     The SERVER's buy total, not a sum over `lines`, for the reason the three
     figures above are the server's: this panel must not hold a second opinion
     about what the costing costs. An absent `costTotals` counts as no cost
     side for the same reason — there is no figure to price from either way. */
  const noCostSide = Number(totals?.buy.amount ?? 0) === 0

  /* What a refused press says, and where it says it.
   *
   * Every condition on the buttons below is LOCAL — lines exist, a cost side
   * exists, the percentage parses. None of them is what the server is bound by:
   * a quote sent from another tab answers 409, a stale build answers 404, a
   * role that may not re-price answers 403. With nothing rendered off
   * `isError`, all three arrive as a button that did nothing, which is exactly
   * how this reached us as "Apply markup changes nothing" against a server path
   * that was working correctly.
   *
   * TWO destinations, because they are two different problems for the reader. A
   * percentage the server rejected is a fault in the box they typed in, so it
   * goes BESIDE that box like every other field refusal in this system
   * (EXPERIENCE.md § State Patterns: field errors beside the field, record
   * errors above the record); anything else is a failure of the ACT, which no
   * edit to the field will fix, and belongs above the row as a record-level
   * message. Sending a rejected percentage to the record-level line would send
   * somebody hunting for a fault outside a form that contains it.
   *
   * The first code only. The server sends one problem per field here, and a
   * field that grew a second would be reporting two faults about one number —
   * which is a message to write when it exists rather than a list to render
   * speculatively. */
  const markupFieldCode = markupMutation.isError
    ? fieldErrors(markupMutation.error)['markupPercent']?.[0]
    : undefined
  /* Anything the field cannot hold. A validation refusal naming some OTHER
     field lands here too, deliberately: this form has one input, so a refusal
     about anything else is not something the reader can act on in it. */
  const markupFailed = markupMutation.isError && markupFieldCode === undefined

  const percent = (value: string | undefined) =>
    value === undefined ? t('common.empty') : `${formatNumber(value, 2)}%`

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <Percent size={15} className="text-text-muted" aria-hidden />
            {t('quotes.pricing.title')}
          </span>
        </CardTitle>
        {/* What the percentage below MEANS, moved off the field's hint and up
            here. It is a statement about the card rather than a note about the
            box — and with the hint gone the input, the chips and the button
            share one baseline instead of the button hanging below a line of
            small text. */}
        <CardSubtitle>{t('quotes.pricing.markupHint')}</CardSubtitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-4 pt-2">
        {/* The server's three figures, read and never recomputed. Two of the
            three are money and one is a percentage, which is why the margin does
            not carry a currency: it is a ratio, not an amount.

            Shown unconditionally, and the `showTotals` prop that once hid them
            is not coming back. QuoteAsideCards' money card prints the same
            three figures in the detail page's right rail, which is the
            arrangement the prop existed for — but the rail is a READING
            surface, and this is the only place the figures stand beside the
            field and the button that change them. Hiding them here would leave
            the reader pricing a costing while looking away from it. Both read
            `record.costTotals` and neither counts, so the repetition cannot
            turn into two answers. */}
        <dl className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-0.5">
            <dt className="asa-eyebrow">{t('quotes.pricing.cost')}</dt>
            <dd className="asa-num text-text-body text-[15px] leading-none">
              {formatNumber(totals?.buy.amount, 2)}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            {/* The COSTING's own word for this figure, not a second one of
                this panel's. `totals.sell` here and the figure under the heavy
                rule in CostLineEditor's footer are the same value off the same
                record, and the costing tab now mounts both — so they are worded
                from one key, and a reword moves both together. */}
            <dt className="asa-eyebrow">{t('quotes.costLines.customerTotal')}</dt>
            <dd className="asa-num text-text-heading text-[15px] leading-none font-semibold">
              {formatNumber(totals?.sell.amount, 2)}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            {/* Named for WHAT IT IS rather than "Margin", because the field
                beside it is a percentage too and the pair only reads correctly
                when each says which denominator it used. */}
            <dt className="asa-eyebrow">{t('quotes.pricing.marginOverRevenue')}</dt>
            <dd className="asa-num text-text-heading text-[15px] leading-none font-semibold">
              {percent(totals?.marginPercent)}
            </dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <Field
            label={t('quotes.pricing.markup')}
            error={markupFieldCode ? t(fieldErrorKey(markupFieldCode)) : undefined}
            className="w-full sm:w-48"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          {/* The three usual percentages, as one press each.

              `aria-pressed` rather than a radio group, and deliberately: these
              do not SELECT anything — they fill the box beside them, which the
              reader may then edit to 12.5 without any of the three becoming
              wrong. A radio would claim the field can hold only one of three.
              Compared against the normalised value, so a typed "10,0" still
              lights the chip a Russian keyboard produced. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {MARKUP_PRESETS.map((value) => {
              const chosen = markup.trim().replace(',', '.') === value

              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={chosen}
                  onClick={() => setMarkup(value)}
                  className={cn(
                    'asa-num rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                    chosen
                      ? 'border-brand-border bg-brand-soft text-brand'
                      : 'border-border-default text-text-body hover:bg-surface-hover',
                  )}
                >
                  {value}%
                </button>
              )
            })}
          </div>

          <Button
            variant="secondary"
            onClick={() => markupMutation.mutate(markup.trim())}
            disabled={
              nothingToPrice ||
              noCostSide ||
              !markupUsable ||
              markupMutation.isPending
            }
          >
            {t('quotes.pricing.applyMarkup')}
          </Button>
        </div>

        {/* The record-level refusal, above the guidance and below the press.
            `role="alert"` because it appears in response to an action a reader
            has just taken and may not be looking at — the same treatment
            InvoiceLineEditor gives a refused charge, and the same wording
            source: `describeProblem`, never the problem's own `detail`, which
            is written server-side in English for a log (AD-22, AD-27). */}
        {markupFailed ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {t(describeProblem(markupMutation.error).detailKey)}
          </p>
        ) : null}

        {/* Said BEFORE the press rather than in a dialog after it. A markup is a
            whole-quote figure and applying it to a subset would produce a total
            whose relationship to the percentage nobody can reconstruct a week
            later — so the button re-prices every line, and the sentence beside
            it says so where the reader is already looking.

            THREE sentences, not two, because the two refusals are two different
            problems: a costing with no lines needs a line, and a costing with
            lines but no cost side needs the cost of those lines. One message
            worded to cover both would leave the reader guessing which of them
            they are in, and the second is the one they will actually meet. */}
        {/* The two sentences sit closer to each other than the card's own
            rhythm, because they are one paragraph of guidance about one button
            rather than two sections of the panel. */}
        <div className="flex flex-col gap-1.5">
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {nothingToPrice
              ? t('quotes.pricing.emptyCosting')
              : noCostSide
                ? t('quotes.pricing.noCostSide')
                : t('quotes.pricing.applyMarkupConfirm')}
          </p>

          {/* The markup is not STORED. The workbook keeps it in a cell; this
              schema has no column for it, and a saved cost line writes the one
              typed price to both of its sides. So editing a line after applying
              puts that line back to its cost while its neighbours keep their
              marked-up prices, and the only sign is a margin that quietly
              drops.

              Said here, in the open, rather than in a comment or a toast after
              the fact: the moment the reader needs it is the moment they are
              looking at this button, and a person who does not know it reads
              the next margin as the deal going bad rather than as their own
              edit.

              Not folded into the sentence above, which is the three-way refusal
              message: this holds whichever of those three the reader is in and
              outlives the press. Suppressed only on an empty costing, where
              there is no line to edit and nothing yet to re-apply. */}
          {nothingToPrice ? null : (
            <p className="text-text-muted text-[12px] leading-snug font-light">
              {t('quotes.pricing.markupNotStored')}
            </p>
          )}
        </div>
      </CardBody>

      <CardFooter className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {nothingToInsert
            ? t('quotes.pricing.noPreset')
            : t('quotes.pricing.presetHint')}
        </p>
        <Button
          variant="ghost"
          iconLeft={ListPlus}
          onClick={() =>
            presetMutation.mutate(
              preset.map((template) => ({
                description: template.label,
                /* Both sides at zero, deliberately. The label is what the preset
                   carries; the amounts are this quote's and are entered against
                   the agent's actual price. */
                buy: '0',
                sell: '0',
                quantity: 1,
              })),
            )
          }
          disabled={nothingToInsert || presetMutation.isPending}
        >
          {t('quotes.pricing.preset')}
        </Button>

        {/* The footer's own refusal, on its own line — `w-full` inside the
            wrapping row, so it sits under the pair rather than squeezing the
            sentence and the button into a column. No field variant here: this
            button posts a batch the reader never typed, so there is no box for
            a refusal to sit beside. */}
        {presetMutation.isError ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative w-full rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {t(describeProblem(presetMutation.error).detailKey)}
          </p>
        ) : null}
      </CardFooter>
    </Card>
  )
}
